import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getGroupPostComments,
  addCommentToGroupPost,
  toggleCommentLike,
  toggleGroupPostLike,
  deleteGroupComment,
  setCommentReaction
} from '../../services/firestoreService';
import { handleError, handleSuccess, handleWarning } from '../../services/errorHandlingService';

const GroupComments = ({ post, isLoggedIn, isAdmin, isMember }) => {
  const { currentUser, userProfile } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [postLiked, setPostLiked] = useState(false);
  const [postLikeCount, setPostLikeCount] = useState(post?.likeCount || 0);
  
  // Reply functionality state
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  
  // Reaction functionality state
  const [showReactions, setShowReactions] = useState(null);
  const [reactionTimeout, setReactionTimeout] = useState(null);

  // Available reactions
  const reactions = [
    { emoji: '👍', name: 'like', label: 'Like' },
    { emoji: '❤️', name: 'love', label: 'Love' },
    { emoji: '😂', name: 'laugh', label: 'Laugh' },
    { emoji: '😮', name: 'wow', label: 'Wow' },
    { emoji: '😢', name: 'sad', label: 'Sad' },
    { emoji: '😡', name: 'angry', label: 'Angry' }
  ];

  useEffect(() => {
    if (post?.id) {
      loadComments();
      // Check if current user has liked this post
      setPostLiked(post?.likes?.includes(currentUser?.uid) || false);
      setPostLikeCount(post?.likeCount || 0);
    }
  }, [post?.id, post?.likes, post?.likeCount, currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recursive function to build nested comment tree
  const buildCommentTree = (comments, parentId = null) => {
    return comments
      .filter(comment => {
        // Handle both null and undefined parentCommentId for top-level comments
        if (parentId === null) {
          return comment.parentCommentId === null || comment.parentCommentId === undefined;
        }
        return comment.parentCommentId === parentId;
      })
      .map(comment => ({
        ...comment,
        replies: buildCommentTree(comments, comment.id)
      }));
  };

  const loadComments = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading comments for post:', post.id);
      const commentsData = await getGroupPostComments(post.id);
      console.log('📥 Raw comments data received:', commentsData);
      console.log('📊 Number of comments found:', commentsData.length);

      if (commentsData.length > 0) {
        console.log('📝 First comment sample:', commentsData[0]);
      }

      // Build nested comment tree with unlimited depth
      const nestedComments = buildCommentTree(commentsData, null);
      console.log('🌳 Nested comments built:', nestedComments);
      console.log('🌳 Number of top-level comments:', nestedComments.length);

      setComments(nestedComments);
    } catch (error) {
      console.error('❌ Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle post like/unlike
  const handlePostLike = async () => {
    if (!currentUser || !isMember) {
      handleWarning('You must be a group member to like posts.');
      return;
    }

    try {
      await toggleGroupPostLike(post.id, currentUser.uid);

      // Update local state optimistically
      if (postLiked) {
        setPostLiked(false);
        setPostLikeCount(prev => Math.max(0, prev - 1));
      } else {
        setPostLiked(true);
        setPostLikeCount(prev => prev + 1);
      }
    } catch (error) {
      handleError(error, {
        context: {
          component: 'GroupComments',
          action: 'togglePostLike',
          postId: post.id
        }
      });
    }
  };

  // Handle comment like/unlike
  const handleLikeComment = async (commentId) => {
    if (!currentUser || !isMember) {
      handleWarning('You must be a group member to like comments.');
      return;
    }

    try {
      await toggleCommentLike(commentId, currentUser.uid);
      // Reload comments to update like counts
      await loadComments();
    } catch (error) {
      handleError(error, {
        context: {
          component: 'GroupComments',
          action: 'toggleCommentLike',
          commentId
        }
      });
    }
  };

  // Handle reactions — persists a real { uid: emoji } reaction to Firestore.
  const handleReaction = async (commentId, reactionType) => {
    if (!currentUser || !isMember) {
      handleWarning('You must be a group member to react.');
      return;
    }
    try {
      setShowReactions(null);
      await setCommentReaction(commentId, currentUser.uid, reactionType);
      await loadComments(); // reload to show updated reaction counts
    } catch (error) {
      handleError(error, {
        context: { component: 'GroupComments', action: 'setCommentReaction', commentId }
      });
    }
  };

  // Handle comment deletion
  const handleDeleteComment = async (commentId) => {
    if (!isAdmin) {
      handleWarning('You do not have permission to delete comments.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      try {
        await deleteGroupComment(commentId, post.id);
        await loadComments(); // Reload comments
        handleSuccess('Comment deleted successfully.');
      } catch (error) {
        handleError(error, {
          context: {
            component: 'GroupComments',
            action: 'deleteComment',
            commentId
          }
        });
      }
    }
  };

  // Handle reaction menu hover
  const handleReactionHover = (commentId) => {
    if (reactionTimeout) {
      clearTimeout(reactionTimeout);
    }
    setShowReactions(commentId);
  };

  const handleReactionLeave = () => {
    const timeout = setTimeout(() => {
      setShowReactions(null);
    }, 300); // 300ms delay before hiding
    setReactionTimeout(timeout);
  };

  // Handle reply submission
  const handleReplySubmit = async (e, parentCommentId) => {
    e.preventDefault();

    if (!replyText.trim()) {
      alert('Please enter a reply.');
      return;
    }

    if (!currentUser || !isMember) {
      alert('You must be a member to reply.');
      return;
    }

    setSubmittingReply(true);
    try {
      const replyData = {
        postId: post.id,
        parentCommentId: parentCommentId,
        content: replyText.trim(),
        authorId: currentUser.uid,
        authorName: userProfile?.firstName && userProfile?.lastName
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : currentUser.displayName || currentUser.email || 'Anonymous User',
        authorEmail: currentUser.email,
        authorTitle: userProfile?.jobTitle || '',
        authorCompany: userProfile?.company || '',
        likes: [],
        likeCount: 0,
        isReply: true
      };

      await addCommentToGroupPost(post.id, replyData);
      setReplyText('');
      setReplyingTo(null);
      await loadComments(); // Reload comments
    } catch (error) {
      console.error('Error adding reply:', error);
      alert(`Error adding reply: ${error.message}. Please try again.`);
    } finally {
      setSubmittingReply(false);
    }
  };

  // Handle comment submission
  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!commentText.trim()) {
      handleWarning('Please enter a comment.');
      return;
    }

    if (!currentUser) {
      handleWarning('You must be logged in to comment.');
      return;
    }

    if (!isMember) {
      handleWarning('You must be a group member to comment.');
      return;
    }

    if (!post?.id) {
      handleError(new Error('Post ID not found'), {
        context: {
          component: 'GroupComments',
          action: 'submitComment'
        }
      });
      return;
    }

    setSubmitting(true);
    try {
      const commentData = {
        postId: post.id,
        content: commentText.trim(),
        authorId: currentUser.uid,
        authorName: userProfile?.firstName && userProfile?.lastName
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : currentUser.displayName || currentUser.email || 'Anonymous User',
        authorEmail: currentUser.email,
        authorTitle: userProfile?.jobTitle || '',
        authorCompany: userProfile?.company || '',
        parentCommentId: null, // Top-level comment
        likes: [],
        likeCount: 0
      };

      const commentId = await addCommentToGroupPost(post.id, commentData);
      setCommentText('');

      // Reload comments to show the new comment
      await loadComments();
      handleSuccess('Comment added successfully!');
    } catch (error) {
      handleError(error, {
        context: {
          component: 'GroupComments',
          action: 'submitComment',
          postId: post.id
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Recursive component to render nested comments
  const renderComment = (comment, depth = 0) => {
    const maxDepth = 10; // Prevent infinite nesting
    // Literal Tailwind classes so they survive purge at build time
    const INDENT = ['', 'ml-8', 'ml-16', 'ml-24', 'ml-32', 'ml-40', 'ml-48'];
    const indentClass = INDENT[Math.min(depth, 6)];
    
    return (
      <div key={comment.id} className={`${indentClass} ${depth > 0 ? 'mt-4' : ''}`}>
        <div className="flex">
          <div className="flex-shrink-0 mr-3">
            <div className={`${depth > 0 ? 'h-8 w-8' : 'h-10 w-10'} rounded-full bg-gray-400 text-white flex items-center justify-center text-sm`}>
              {comment.authorName ? comment.authorName.charAt(0).toUpperCase() : 'U'}
            </div>
          </div>
          <div className="flex-grow">
            <div className={`${depth > 0 ? 'bg-gray-50' : 'bg-white'} rounded-lg p-3 ${depth === 0 ? 'border border-gray-200' : ''}`}>
              <div className="flex items-center mb-2">
                <span className={`font-semibold text-gray-900 ${depth > 0 ? 'text-sm' : ''}`}>{comment.authorName}</span>
                {comment.authorTitle && comment.authorCompany && (
                  <>
                    <span className="mx-1 text-gray-500">•</span>
                    <span className={`text-gray-500 ${depth > 0 ? 'text-xs' : 'text-sm'}`}>{comment.authorTitle} at {comment.authorCompany}</span>
                  </>
                )}
              </div>
              <p className={`text-gray-700 ${depth > 0 ? 'text-sm' : ''}`}>{comment.content}</p>
            </div>
            
            {/* Comment Actions */}
            <div className={`mt-2 flex items-center ${depth > 0 ? 'text-xs' : 'text-sm'} text-gray-500`}>
              <span className="mr-4">{formatDate(comment.createdAt)}</span>
              
              {/* Like Button */}
              <button
                className={`mr-4 flex items-center transition duration-200 ${
                  comment.likes?.includes(currentUser?.uid)
                    ? 'text-blue-600'
                    : 'text-gray-500 hover:text-apn-blue'
                } ${!isLoggedIn || !isMember ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => handleLikeComment(comment.id)}
                disabled={!isLoggedIn || !isMember}
              >
                <svg className={`${depth > 0 ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} fill={comment.likes?.includes(currentUser?.uid) ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.60L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                <span>{comment.likeCount || 0}</span>
              </button>
              
              {/* Reply Button */}
              {isMember && depth < maxDepth && (
                <button
                  className="mr-4 text-gray-500 hover:text-apn-blue transition duration-200"
                  onClick={() => {
                    setReplyingTo(replyingTo === comment.id ? null : comment.id);
                    setReplyText('');
                  }}
                >
                  {replyingTo === comment.id ? 'Cancel' : 'Reply'}
                </button>
              )}
              
              {/* Reaction Button with Dropdown */}
              {isMember && (
                <div 
                  className="relative inline-block"
                  onMouseEnter={() => handleReactionHover(comment.id)}
                  onMouseLeave={handleReactionLeave}
                >
                  <button className="text-gray-500 hover:text-apn-blue transition duration-200">
                    React
                  </button>
                  
                  {/* Reaction Dropdown */}
                  {showReactions === comment.id && (
                    <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex space-x-1 z-10">
                      {reactions.map((reaction) => (
                        <button
                          key={reaction.name}
                          onClick={() => handleReaction(comment.id, reaction.name)}
                          className="p-2 hover:bg-gray-100 rounded transition duration-200"
                          title={reaction.label}
                        >
                          <span className="text-lg">{reaction.emoji}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Reaction summary (counts) */}
              {comment.reactions && Object.keys(comment.reactions).length > 0 && (
                <span className="mr-4 flex items-center space-x-2">
                  {reactions
                    .map((r) => ({ ...r, count: Object.values(comment.reactions).filter((v) => v === r.name).length }))
                    .filter((r) => r.count > 0)
                    .map((r) => (
                      <span key={r.name} className="text-sm" title={r.label}>{r.emoji} {r.count}</span>
                    ))}
                </span>
              )}

              {/* Delete Button for Admins */}
              {isAdmin && (
                <button
                  className="text-red-500 hover:text-red-700"
                  onClick={() => handleDeleteComment(comment.id)}
                >
                  Delete
                </button>
              )}
            </div>
            
            {/* Reply Form */}
            {replyingTo === comment.id && (
              <div className="mt-4">
                <form onSubmit={(e) => handleReplySubmit(e, comment.id)} className="flex">
                  <div className="flex-shrink-0 mr-3">
                    <div className="h-8 w-8 rounded-full bg-apn-blue text-white flex items-center justify-center text-sm">
                      {userProfile?.firstName ? userProfile.firstName.charAt(0).toUpperCase() : 'U'}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={`Reply to ${comment.authorName}...`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue resize-none"
                      rows="2"
                      disabled={submittingReply}
                    />
                    <div className="mt-2 flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                        }}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                        disabled={submittingReply}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-1 bg-apn-blue text-white text-sm rounded hover:bg-blue-700 transition duration-200 disabled:opacity-50"
                        disabled={submittingReply || !replyText.trim()}
                      >
                        {submittingReply ? 'Posting...' : 'Reply'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}
            
            {/* Render Nested Replies Recursively */}
            {comment.replies && comment.replies.length > 0 && (
              <div className="mt-4">
                {comment.replies.map(reply => renderComment(reply, depth + 1))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Post Reactions */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              className={`flex items-center transition duration-200 ${
                postLiked ? 'text-blue-600' : 'text-gray-500 hover:text-apn-blue'
              } ${!isLoggedIn || !isMember ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handlePostLike}
              disabled={!isLoggedIn || !isMember}
            >
              <svg className="h-5 w-5 mr-1" fill={postLiked ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.60L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
              <span>{postLikeCount}</span>
            </button>
            <span className="text-gray-500">{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="p-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-apn-blue mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Be the first to comment on this discussion!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {comments.map((comment) => renderComment(comment))}
          </div>
        )}
        
        {/* Comment Form - Only for members */}
        {isMember ? (
          <div className="mt-6">
            <form onSubmit={handleCommentSubmit} className="flex">
              <div className="flex-shrink-0 mr-3">
                <div className="h-10 w-10 rounded-full bg-apn-blue text-white flex items-center justify-center">
                  {userProfile?.firstName ? userProfile.firstName.charAt(0).toUpperCase() :
                   currentUser?.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
                </div>
              </div>
              <div className="flex-grow">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-apn-blue resize-none"
                  rows="3"
                  disabled={submitting}
                />
                <div className="mt-3 flex justify-end">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-apn-blue text-white rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50"
                    disabled={submitting || !commentText.trim()}
                  >
                    {submitting ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        ) : (
          <div className="mt-6 text-center py-4 bg-gray-50 rounded-lg">
            <p className="text-gray-500">
              {isLoggedIn ? 'You must be a member of this group to comment.' : 'Please log in to comment.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupComments;
